import { useState, useCallback } from "react";
import { DEFAULT_TRAIL_COLOR } from "../lib/palette.js";
import { CLUSTER_PRESET_DEFAULT } from "../lib/leaflet/clusterConfig.js";
import { migrateClusterSettings } from "./migrateClusterSettings.js";

const KEY = "gps_user_settings";

// Trail length defaults: short cola (5). Historical range 10–200 was excessive
// for onboarding. Map toolkit owns the show/hide toggle (see useMapPreferences
// `showTrails`); this setting controls ONLY the cola length + color.
//
// `clampTrail` normalizes legacy / corrupt values on read so the rest of the
// app never sees NaN, negative, zero, or out-of-range counts.
const TRAIL_MIN = 1;
const TRAIL_MAX = 10;
const DEFAULTS = {
  tracePositions: 5,
  trailColor: DEFAULT_TRAIL_COLOR,
  clusterEnabled: true,
  clusterPreset: CLUSTER_PRESET_DEFAULT,
};

function clampTrail(parsed) {
  const raw = parsed.tracePositions;
  if (raw === undefined) return parsed;
  if (!Number.isFinite(raw) || raw < TRAIL_MIN) {
    return { ...parsed, tracePositions: DEFAULTS.tracePositions };
  }
  if (raw > TRAIL_MAX) {
    return { ...parsed, tracePositions: TRAIL_MAX };
  }
  return parsed;
}

export function useSettings() {
  const [settings, setSettings] = useState(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return DEFAULTS;
      const parsed = JSON.parse(raw);
      const migrated = clampTrail(migrateClusterSettings(parsed));
      return { ...DEFAULTS, ...migrated };
    } catch {
      return DEFAULTS;
    }
  });

  const updateSettings = useCallback((updates) => {
    setSettings((prev) => {
      const next = { ...prev, ...updates };
      localStorage.setItem(KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return { settings, updateSettings };
}
